/**
 * A cara da barbearia.
 *
 * Logo, ícone e cores — as três coisas que fazem o app parecer da barbearia em
 * vez de um sistema genérico com o nome dela em cima. A especificação previa
 * isso desde a §6 e listava como pendência da fase 3; é esta tela.
 *
 * Duas decisões de interface valem o comentário:
 *
 *  - **Paletas antes do seletor livre.** Seis combinações prontas resolvem em
 *    um toque, que é o que o barbeiro consegue fazer em pé. Quem tem a cor
 *    exata da fachada abre o seletor nativo e digita — mas ninguém começa
 *    diante de um espectro em branco.
 *  - **Prévia ao vivo, aplicada de verdade.** Tocar numa paleta repinta o app
 *    inteiro na hora, antes de salvar. Um quadradinho de amostra mentiria: a
 *    cor que importa é a do botão principal sobre o fundo real.
 */

import { buildTenantTheme, PALETTES } from '@barbearia/core';
import {
  HOME_SCREEN_LABEL_LIMIT,
  PUBLIC_CATALOG_LABELS,
  type PublicCatalog,
  type ThemeMode,
} from '@barbearia/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ScreenHeader } from '../../App.tsx';
import { PhotoPicker } from '../../components/PhotoPicker.tsx';
import { Scissors } from '../../components/TenantMark.tsx';
import { Button, ErrorNote, Field } from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useRefreshSession, useTenant } from '../../lib/contexts.tsx';
import { BRAND_RESIZE } from '../../lib/image.ts';
import { applyTheme, resolveMode } from '../../lib/theme.ts';

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'SYSTEM', label: 'Do celular' },
  { value: 'LIGHT', label: 'Claro' },
  { value: 'DARK', label: 'Escuro' },
];

const CATALOG_OPTIONS: PublicCatalog[] = ['SERVICES_AND_PRICES', 'SERVICES', 'HIDDEN'];

export function Branding() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const refresh = useRefreshSession();

  const [primary, setPrimary] = useState(tenant.primaryColor);
  const [accent, setAccent] = useState(tenant.accentColor);
  const [themeMode, setThemeMode] = useState<ThemeMode>(tenant.themeMode);
  const [shortName, setShortName] = useState(tenant.shortName ?? '');
  const [error, setError] = useState<string | null>(null);

  const dirty =
    primary !== tenant.primaryColor ||
    accent !== tenant.accentColor ||
    themeMode !== tenant.themeMode ||
    shortName.trim() !== (tenant.shortName ?? '');

  // A prévia é o app inteiro repintado. Ao sair da tela sem salvar, o efeito de
  // limpeza devolve as cores gravadas — senão o barbeiro carregaria a cor que
  // desistiu de usar até recarregar a página.
  useEffect(() => {
    applyTheme(buildTenantTheme({ primary, accent }), resolveMode(themeMode));
    return () => {
      applyTheme(
        buildTenantTheme({ primary: tenant.primaryColor, accent: tenant.accentColor }),
        resolveMode(tenant.themeMode),
      );
    };
  }, [primary, accent, themeMode, tenant.primaryColor, tenant.accentColor, tenant.themeMode]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/tenant/branding', {
        primaryColor: primary,
        accentColor: accent,
        themeMode,
        // Vazio apaga: o atalho volta a se chamar como a barbearia.
        shortName: shortName.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenant'] });
      setError(null);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  const upload = useMutation({
    mutationFn: (input: { field: 'logo' | 'icon'; data: string }) =>
      api.put(`/tenant/${input.field}`, { data: input.data }),
    onSuccess: () => refresh(),
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  const remove = useMutation({
    mutationFn: (field: 'logo' | 'icon') => api.delete(`/tenant/${field}`),
    onSuccess: () => refresh(),
  });

  return (
    <div className="flex min-h-full flex-col pb-28">
      <ScreenHeader title="Identidade visual" subtitle="Como a barbearia aparece no app" />

      <section className="px-4 pt-5">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Logo
        </h2>
        <div className="flex items-center gap-4 rounded-2xl border border-line bg-surface-raised p-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-primary/12">
            {tenant.logoUrl ? (
              <img src={tenant.logoUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <Scissors size={44} />
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <PhotoPicker
              label={tenant.logoUrl ? 'Trocar logo' : 'Escolher logo'}
              options={BRAND_RESIZE}
              facing="environment"
              busy={upload.isPending}
              onPicked={(data) => upload.mutateAsync({ field: 'logo', data })}
            />
            {tenant.logoUrl && (
              <button
                type="button"
                onClick={() => remove.mutate('logo')}
                className="touch-target text-[14px] text-ink-faint"
              >
                Remover
              </button>
            )}
          </div>
        </div>
        <p className="px-1 pt-2 text-[12px] text-ink-faint">
          Aparece na tela de entrada, no cabeçalho e no atalho instalado no celular. Sem logo, o app
          usa a tesoura na cor da barbearia.
        </p>
      </section>

      <section className="px-4 pt-6">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Atalho no celular
        </h2>
        <div className="flex items-center gap-4 rounded-2xl border border-line bg-surface-raised p-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/12">
            {tenant.iconUrl ? (
              <img src={tenant.iconUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <Scissors size={30} />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <PhotoPicker
              label={tenant.iconUrl ? 'Trocar ícone' : 'Usar um ícone diferente'}
              options={BRAND_RESIZE}
              busy={upload.isPending}
              onPicked={(data) => upload.mutateAsync({ field: 'icon', data })}
            />
            {tenant.iconUrl && (
              <button
                type="button"
                onClick={() => remove.mutate('icon')}
                className="touch-target text-[14px] text-ink-faint"
              >
                Remover
              </button>
            )}
          </div>
        </div>
        <p className="px-1 pt-2 text-[12px] text-ink-faint">
          Só se a logo não funcionar como quadrado pequeno. Sem ícone próprio, o atalho usa a logo.
        </p>

        <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-line bg-surface-raised p-4">
          <Field
            label="Nome embaixo do ícone"
            value={shortName}
            onChange={(event) => setShortName(event.target.value)}
            placeholder={tenant.name}
            maxLength={30}
            hint={`Vazio usa o nome da barbearia. O celular corta perto de ${HOME_SCREEN_LABEL_LIMIT} letras.`}
          />
          <HomeScreenPreview label={shortName.trim() || tenant.name} />
        </div>
      </section>

      <section className="px-4 pt-6">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Cores
        </h2>

        <div className="grid grid-cols-3 gap-2">
          {PALETTES.map((palette) => {
            const active = palette.primary === primary && palette.accent === accent;
            return (
              <button
                key={palette.id}
                type="button"
                onClick={() => {
                  setPrimary(palette.primary);
                  setAccent(palette.accent);
                }}
                className={`flex touch-target flex-col items-center gap-2 rounded-xl border px-2 py-3 ${
                  active ? 'border-primary bg-primary/10' : 'border-line bg-surface-raised'
                }`}
              >
                <span className="flex gap-1">
                  <span
                    className="h-6 w-6 rounded-full"
                    style={{ backgroundColor: palette.primary }}
                  />
                  <span
                    className="h-6 w-6 rounded-full"
                    style={{ backgroundColor: palette.accent }}
                  />
                </span>
                <span className="text-[12px] font-medium text-ink-soft">{palette.name}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-3 overflow-hidden rounded-xl border border-line">
          <ColorRow
            label="Cor principal"
            hint="Botões, destaques e a barra do atalho"
            value={primary}
            onChange={setPrimary}
          />
          <ColorRow
            label="Cor de apoio"
            hint="Links e informações secundárias"
            value={accent}
            onChange={setAccent}
          />
        </div>

        <p className="px-1 pt-2 text-[12px] text-ink-faint">
          O app corrige o tom quando a cor escolhida não teria contraste suficiente no tema claro ou
          no escuro — dá para escolher qualquer cor sem deixar o app ilegível.
        </p>
      </section>

      <section className="px-4 pt-6">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Tema
        </h2>
        <div className="flex gap-2">
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setThemeMode(option.value)}
              className={`flex-1 touch-target rounded-xl border px-3 text-[14px] font-medium ${
                themeMode === option.value
                  ? 'border-primary bg-primary/10 text-ink'
                  : 'border-line bg-surface-raised text-ink-soft'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="px-1 pt-2 text-[12px] text-ink-faint">
          É o padrão da barbearia. Cada pessoa ainda pode escolher o dela em Ajustes.
        </p>
      </section>

      <PublicCatalogSection />

      {error && (
        <div className="px-4 pt-4">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}

      {/*
        A barra só existe quando há o que salvar. Um botão fixo desabilitado
        fica translúcido por cima do conteúdo que rola atrás — na primeira
        versão desta tela ele cobria a linha "Cor principal" com um "Tudo
        salvo" ilegível. Sem alteração pendente, o lugar simplesmente não é
        ocupado.
      */}
      {dirty && (
        <div className="pb-safe sticky bottom-0 z-40 border-t border-line bg-surface px-4 pt-3">
          <Button full className="shadow-lg" loading={save.isPending} onClick={() => save.mutate()}>
            Salvar
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * O que a página pública mostra do catálogo.
 *
 * Mora nesta tela e não junto das regras da agenda porque é decisão de vitrine,
 * não de funcionamento: quem vem aqui está pensando em como a barbearia
 * aparece para quem ainda não é cliente.
 */
function PublicCatalogSection() {
  const queryClient = useQueryClient();
  const [value, setValue] = useState<PublicCatalog | null>(null);

  const current = value;

  const update = useMutation({
    mutationFn: (next: PublicCatalog) => api.patch('/tenant/settings', { publicCatalog: next }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenant'] });
      await queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
  });

  const { tenant } = useTenant();
  const selected = current ?? tenant.publicCatalog;

  return (
    <section className="px-4 pt-6">
      <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
        Serviços na página pública
      </h2>
      <div className="flex flex-col gap-2">
        {CATALOG_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => {
              setValue(option);
              update.mutate(option);
            }}
            className={`flex touch-target items-center justify-between rounded-xl border px-4 text-left text-[15px] ${
              selected === option
                ? 'border-primary bg-primary/10 text-ink'
                : 'border-line bg-surface-raised text-ink-soft'
            }`}
          >
            {PUBLIC_CATALOG_LABELS[option]}
            {selected === option && <span className="text-primary">✓</span>}
          </button>
        ))}
      </div>
      <p className="px-1 pt-2 text-[12px] text-ink-faint">
        Vale para quem abre o endereço da barbearia sem estar logado.
      </p>
    </section>
  );
}

function ColorRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-4 border-b border-line bg-surface-raised px-4 py-3 last:border-b-0">
      <span className="min-w-0">
        <span className="block text-[15px] text-ink">{label}</span>
        <span className="block text-[12px] text-ink-faint">{hint}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <span className="text-[13px] tabular-nums text-ink-faint uppercase">{value}</span>
        <input
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className="h-10 w-12 cursor-pointer rounded-lg border border-line bg-transparent"
        />
      </span>
    </label>
  );
}

/**
 * Como o atalho vai ficar na tela de início.
 *
 * Quem corta o rótulo é o CSS, numa largura de ícone de verdade — contar letras
 * mentiria, porque "Wilhelmina" e "iiiiiiiiii" não ocupam o mesmo espaço. E é
 * por isso que a prévia existe: o dono decide o que sobra do nome dele vendo o
 * resultado, em vez de descobrir depois de instalar.
 */
function HomeScreenPreview({ label }: { label: string }) {
  const { tenant } = useTenant();
  const icon = tenant.iconUrl ?? tenant.logoUrl;

  return (
    <div className="flex items-center gap-4">
      <div className="flex w-[76px] shrink-0 flex-col items-center gap-1.5">
        <div className="flex h-[60px] w-[60px] items-center justify-center overflow-hidden rounded-[14px] bg-primary/12">
          {icon ? (
            <img src={icon} alt="" className="h-full w-full object-cover" />
          ) : (
            <Scissors size={34} />
          )}
        </div>
        <span className="w-full truncate text-center text-[11px] leading-tight text-ink">
          {label}
        </span>
      </div>

      <p className="text-[12px] leading-snug text-ink-faint">
        É assim que o app aparece na tela de início de quem instalar.
      </p>
    </div>
  );
}
